// Provider-delegation lifecycle for a finding (ADR-0074).
//
// This vocabulary is an engine constant rather than a recipe primitive,
// and the asymmetry with `recipe.observationStatuses` (ADR-0058) is
// deliberate. Two reasons, in order of weight:
//
//   1. Delegation status is *predicated on* in cross-org SQL aggregates
//      that have no recipe in scope — the open-findings counts on
//      /facility-dashboard and the ia-chat grounding block both filter
//      `delegation_status NOT IN (…)`. A recipe-declared key set cannot
//      appear in those predicates without joining the org's recipe into
//      every aggregate. `observationStatus`, by contrast, is only ever
//      projected, never filtered — which is why it can be recipe-owned.
//
//   2. Delegation carries a transition map whose edges depend on the
//      actor (a public share link may report work done; only an
//      authenticated user may accept it). A state machine is mechanism,
//      and mechanism belongs to the engine. `observationStatuses` is
//      flat content: keys, labels, a terminal flag.
//
// If a future vertical needs a different *key set*, promote this to a
// recipe primitive shaped like `observationStatusSchema` — but budget
// the two cross-org aggregates first, because they are the real cost.
// If a vertical needs different *labels* over the same keys, that is an
// `organizations.config` override, not a primitive: keys are persisted
// in `findings.delegation_status` and `finding_events.{from,to}_status`
// and queried cross-org; labels are never persisted.
import { ConflictError } from "@bitacorapro/shared";
import { z } from "zod";
/**
 * The delegation lifecycle, in workflow order. `null` on the column is a
 * sixth state — "never delegated" — which is deliberately not a member
 * here: it is the absence of a value, not a value, and no caller may
 * write it through the status endpoint.
 */
export const DELEGATION_STATUSES = [
    "pendiente",
    "en_curso",
    "reportado_resuelto",
    "resuelto",
    "anulado",
];
export const delegationStatusSchema = z.enum(DELEGATION_STATUSES);
/**
 * Terminal states. A finding in one of these is closed and drops out of
 * every "abiertas" count in the product. `null` counts as OPEN — a
 * never-delegated finding is outstanding work, not finished work.
 */
export const CLOSED_DELEGATION_STATUSES = [
    "resuelto",
    "anulado",
];
/** True when a finding is still outstanding. `null` (never delegated) is open. */
export function isOpenDelegationStatus(status) {
    if (status == null)
        return true;
    return !CLOSED_DELEGATION_STATUSES.includes(status);
}
/**
 * Statuses a client may write through POST /findings/:id/delegation-status.
 * `pendiente` is excluded on purpose: it means "assigned to a provider,
 * not yet started", so it is only ever written by POST /findings/:id/delegate,
 * which is the call that supplies the provider.
 */
export const CLIENT_SETTABLE_DELEGATION_STATUSES = [
    "en_curso",
    "reportado_resuelto",
    "resuelto",
    "anulado",
];
export const clientSettableDelegationStatusSchema = z.enum(CLIENT_SETTABLE_DELEGATION_STATUSES);
/** Key used for the `null` (never-delegated) origin in the transition map. */
const NEVER_DELEGATED = "__null__";
/**
 * Legal edges, keyed by origin. Sources: the lifecycle documented on
 * `findings.delegation_status` and the two-stage close on `finding_events`.
 *
 * Notes on the less obvious edges:
 *   - From `null`, a gerente may close directly. The facility recipe
 *     declares `observationStatuses: []` and states that delegationStatus
 *     already covers post-sign tracking, so there is no other endpoint
 *     that could record "resolved without ever assigning anyone".
 *   - `resuelto`/`anulado` → `en_curso` is the reopen edge. The status
 *     enum stays small (no `reabierto` value) because the event row
 *     carries the reopen semantics explicitly.
 *   - Reopening a finding that never had a provider lands in `en_curso`
 *     rather than back at `null`. `null` is unreachable by design: it
 *     means "never delegated", and that is a fact about history, not a
 *     state you can return to.
 */
export const DELEGATION_TRANSITIONS = Object.freeze({
    [NEVER_DELEGATED]: ["en_curso", "resuelto", "anulado"],
    pendiente: ["en_curso", "reportado_resuelto", "resuelto", "anulado"],
    en_curso: ["reportado_resuelto", "resuelto", "anulado"],
    reportado_resuelto: ["en_curso", "resuelto", "anulado"],
    resuelto: ["en_curso", "anulado"],
    anulado: ["en_curso"],
});
/**
 * The ceiling a public share link may reach. The proveedor can report
 * work done; accepting that report is the client's act and requires an
 * authenticated user. Enforced here rather than in the route so the
 * future proveedor-portal endpoint inherits it instead of having to
 * remember to copy a route-local check.
 */
export const SHARE_LINK_MAX_STATUS = "reportado_resuelto";
/**
 * The statuses `from` may legally move to, for this actor, restricted to
 * what a client may actually send.
 *
 * Exported instead of `DELEGATION_TRANSITIONS` itself so the `__null__`
 * origin key stays private. A consumer that reads the raw map has to
 * re-declare that sentinel, which is the same duplication-by-magic-string
 * this module exists to eliminate — one layer down. Carrying `actor`
 * here also means a future proveedor-portal UI cannot offer targets its
 * own endpoint would reject.
 */
export function legalDelegationTargets(from, actor = "user") {
    const allowed = DELEGATION_TRANSITIONS[from ?? NEVER_DELEGATED] ?? [];
    return allowed.filter((to) => CLIENT_SETTABLE_DELEGATION_STATUSES.includes(to) &&
        (actor !== "share_link" || to === SHARE_LINK_MAX_STATUS));
}
/**
 * Enforce one delegation-status transition.
 *
 * Deliberately takes no `Recipe`. The absence of that parameter — next
 * to `resolveObservationStatus(recipe, status)`, which needs one — is
 * the in-code marker of which vocabulary is engine-owned and which is
 * recipe-declared. Adding a recipe parameter here "for consistency"
 * would quietly open the door to a per-recipe key set and break the two
 * cross-org aggregates described at the top of this file.
 */
export function validateDelegationTransition(input) {
    const { from, to, actor } = input;
    if (!DELEGATION_STATUSES.includes(to)) {
        throw new ConflictError({
            code: "INVALID_DELEGATION_TRANSITION",
            message: `Unknown delegation status "${to}"`,
        });
    }
    if (actor === "share_link" && to !== SHARE_LINK_MAX_STATUS) {
        throw new ConflictError({
            code: "DELEGATION_ACTOR_FORBIDDEN",
            message: `A share link may only set "${SHARE_LINK_MAX_STATUS}", not "${to}"`,
        });
    }
    const allowed = DELEGATION_TRANSITIONS[from ?? NEVER_DELEGATED] ?? [];
    if (!allowed.includes(to)) {
        throw new ConflictError({
            code: "INVALID_DELEGATION_TRANSITION",
            message: `Cannot move a finding from "${from ?? "sin asignar"}" to "${to}"`,
        });
    }
}
/**
 * The `finding_events.event_type` that records a transition.
 *
 * `resolved_direct` distinguishes a close that skipped the proveedor's
 * self-report from one that accepted it. Both are legitimate, but they
 * carry different evidentiary weight, and "show me every closure nobody
 * else attested" should be a predicate rather than an inference over
 * `from_status`.
 */
export function delegationEventType(from, to) {
    if (to === "en_curso") {
        return from === "resuelto" || from === "anulado" ? "reopened" : "in_progress";
    }
    if (to === "reportado_resuelto")
        return "reported_resolved";
    if (to === "resuelto") {
        return from === "reportado_resuelto" ? "resolved" : "resolved_direct";
    }
    if (to === "anulado")
        return "cancelled";
    return "assigned";
}
/**
 * True when the transition needs an explicit note. A close that bypasses
 * the proveedor's self-report is the client asserting the work is done on
 * its own authority; the trail should say on what basis.
 */
export function delegationNoteRequired(from, to) {
    return to === "resuelto" && from !== "reportado_resuelto";
}
//# sourceMappingURL=delegation.js.map